import { useQueries } from "@tanstack/react-query";
import { Box, Container, Skeleton } from "@mui/material";
import { useOutletContext } from "react-router-dom";

import Carousel from "../../components/Carousel";
import ActivityCarouselCard from "./CoverActivity";
import CollectionCarouselCard from "./CoverCollection";
import ExploreNewHeader from "./Header";
import Offline from "../../components/Offline";
import { EXPLORE_SECTIONS } from "./exploreSections";
import useApi from "../../hooks/useApi";
import { getContentTypeSlug } from "../../utils/utils";
import { useDetailNavigation } from "../../hooks/useDetailNavigation";

// `preview` caps the activity feed to one card per author at the DB (interim stand-in for
// proper same-author bundling), so a single user's burst can't fill every slot.
const ACTIVITY_PREVIEW_COUNT = 5;

/**
 * Build the React Query options for a single explore section.
 *
 * Keys/fns mirror what the section components previously owned, so the cache is
 * unchanged — they're lifted to the parent only so it can observe every section's
 * state at once (e.g. to show the Offline screen when they all network-error).
 */
function buildSectionQuery(section, contentTypeFilter, api) {
  if (section.type === "activity") {
    return {
      queryKey: [
        "exploreActivityPreview",
        contentTypeFilter,
        ACTIVITY_PREVIEW_COUNT,
      ],
      queryFn: () =>
        api.fetchActivityFeed({
          contentType: contentTypeFilter,
          scope: "everyone",
          preview: ACTIVITY_PREVIEW_COUNT,
        }),
    };
  }

  // "collection" — the only other live section type (pickOfMonth is deferred).
  return {
    queryKey: ["exploreCollections", section.slug, contentTypeFilter],
    queryFn: () =>
      api.fetchExploreCollections({
        tagFilter: section.tagFilter,
        contentType: contentTypeFilter,
        perPage: 5,
        // Cap each list's items to the 5 posters in the carousel card; the
        // response still carries the true item_count and full-list
        // finished_count used for the card's "% finished".
        maxItems: 5,
        // Featured carousel — hide collections too sparse to make a
        // convincing cover. The "see all" detail page shows every collection.
        minItems: 4,
        sortBy: section.sortBy,
      }),
  };
}

/**
 * Skeleton placeholder for a collection carousel section, shown while the
 * query is in flight. Matches the structural dimensions of the real carousel
 * so the page doesn't shift when content arrives.
 */
function CollectionCarouselSkeleton() {
  return (
    <Box>
      <Skeleton variant="text" sx={{ fontSize: "1.25rem" }} width={250} />
      <Skeleton
        variant="text"
        sx={{ fontSize: "0.875rem", mb: 0.75 }}
        width="70%"
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 1.5,
          overflow: "hidden",
          // Matches the real carousel's bleed-disabling at "lg" (see components/Carousel.js)
          // so the skeleton doesn't shift layout once the real content loads in.
          mx: { xs: -2, sm: -3, lg: 0 },
          "& > :first-of-type": { ml: { xs: 2, sm: 3, lg: 0 } },
        }}
      >
        {[0, 1, 2].map((i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            sx={{
              width: { xs: "65vw", sm: "300px" },
              height: "170px",
              borderRadius: 1,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

/**
 * A single collection carousel section. Presentational: its query lives in the
 * parent and is passed in via `result`. Shows a skeleton while loading; hidden
 * entirely when the section returns no data.
 */
function CollectionSection({ section, result, contentSlug, onCollectionClick }) {
  const items = result.data?.items ?? [];

  if (result.isPending) return <CollectionCarouselSkeleton />;
  if (items.length === 0) return null;

  // Transform API list shape → CoverCollection card props
  const cardItems = items.map((list) => ({
    list_id: list.list_id,
    title: list.title,
    contentType: list.content_type,
    // item_count is the true total (items[] is capped to maxItems); fall back gracefully
    itemCount: list.item_count ?? list.items.length,
    // finished_count is omitted for logged-out viewers; null tells the card to render placeholder copy
    finishedCount: list.finished_count ?? null,
    posters: list.items.slice(0, 5).map((i) => i.image_url),
  }));

  return (
    <Carousel
      title={section.title}
      description={section.previewDescription}
      items={cardItems}
      getItemKey={(card) => card.list_id}
      renderItem={(card) => (
        <CollectionCarouselCard
          {...card}
          titleLines={section.titleLines}
          onClick={() => onCollectionClick?.(card)}
        />
      )}
      seeMoreLink={`/explore/${section.slug}${contentSlug ? `/${contentSlug}` : ""}`}
    />
  );
}

/**
 * Activity feed preview section. Presentational: its query lives in the parent
 * and is passed in via `result`. Shows the global "everyone" feed, so it renders
 * for logged-out visitors too; hidden (along with the taste-matching teaser
 * banner beneath it) only while the feed is loading/empty, so the banner never
 * pops in above the carousel and jumps down once it loads.
 */
function ActivitySection({
  section,
  result,
  contentSlug,
  onActivityItemClick,
  onPosterClick,
}) {
  const items = (result.data?.items ?? [])
    .filter((i) => i.activity_type === "journal")
    .slice(0, ACTIVITY_PREVIEW_COUNT);

  if (items.length === 0) return null;

  return (
    <Box>
      <Carousel
        title={section.title}
        items={items}
        getItemKey={(item, index) => item.id ?? index}
        renderItem={(item) => (
          <ActivityCarouselCard
            {...item}
            onCardClick={() => onActivityItemClick?.(item)}
            onPosterClick={() => onPosterClick?.(item)}
          />
        )}
        seeMoreLink={`/explore/${section.seeMoreSlug}${contentSlug ? `/${contentSlug}` : ""}`}
      />
    </Box>
  );
}

/**
 * Main explore home page — renders section carousels from EXPLORE_SECTIONS.
 * Sections show skeletons while loading and are hidden entirely when they
 * return no data. When every section network-errors (offline), the page shows
 * the Offline screen instead of an empty body.
 */
export default function Explore({ contentTypeFilter = "all" }) {
  const { toggleDrawer, drawerWidth } = useOutletContext();
  const api = useApi();
  const { openList, openContent } = useDetailNavigation();

  // Carried into "see all" links so drilling into a section keeps the active filter
  const contentSlug =
    contentTypeFilter !== "all" ? getContentTypeSlug(contentTypeFilter) : null;

  // Section queries are lifted here so we can detect a page-wide network failure.
  const sectionResults = useQueries({
    queries: EXPLORE_SECTIONS.map((section) =>
      buildSectionQuery(section, contentTypeFilter, api),
    ),
  });

  // Every section failed with nothing cached to show → treat the whole page as offline.
  // (A single cached section keeps `every` false, so we still render what we have.)
  const allSectionsErrored =
    sectionResults.length > 0 && sectionResults.every((r) => r.isError);

  return (
    <Container maxWidth="lg" disableGutters sx={{ pb: 6 }}>
      <ExploreNewHeader
        toggleDrawer={toggleDrawer}
        drawerWidth={drawerWidth}
        activeFilter={contentTypeFilter}
        basePath="/explore"
      />

      {allSectionsErrored ? (
        <Offline />
      ) : (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {EXPLORE_SECTIONS.map((section, index) => {
            const result = sectionResults[index];

            if (section.type === "activity") {
              return (
                <ActivitySection
                  key="activity"
                  section={section}
                  result={result}
                  contentSlug={contentSlug}
                  // Tapping a card opens its entry in LibraryModal (a feed
                  // item's id is its journal id) — focused on its own, or
                  // scoped on the You tab if it's the viewer's own entry.
                  onActivityItemClick={(item) =>
                    openContent({
                      contentId: item.content_id,
                      contentType: item.content_type,
                      title: item.content_title,
                      entryId: item.id,
                    })
                  }
                  onPosterClick={(item) =>
                    openContent({
                      contentId: item.content_id,
                      contentType: item.content_type,
                      title: item.content_title,
                    })
                  }
                />
              );
            }

            if (section.type === "collection") {
              return (
                <CollectionSection
                  key={section.slug}
                  section={section}
                  result={result}
                  contentSlug={contentSlug}
                  onCollectionClick={(card) =>
                    openList({ list_id: card.list_id, title: card.title })
                  }
                />
              );
            }

            return null;
          })}
        </Box>
      )}
    </Container>
  );
}
